import { defaultLayer } from '@strength/db-schema';
/**
 * Merges a block layer and an athlete layer and produces a WorkoutLayer object (instead of the normal Workout type)
 * The end result is a block layer (only *Add components)
 * If either layer is undefined, we simply return the other layer.
 */
export function mergeAthleteToBlockLayer(blockWorkoutLayer, athleteWorkoutLayer) {
    const blockLayer = blockWorkoutLayer && 'layer' in blockWorkoutLayer
        ? blockWorkoutLayer.layer
        : blockWorkoutLayer;
    const athleteLayer = athleteWorkoutLayer && 'layer' in athleteWorkoutLayer
        ? athleteWorkoutLayer.layer
        : athleteWorkoutLayer;
    if (isLayerEmpty(blockLayer) && isLayerEmpty(athleteLayer))
        return defaultLayer();
    if (isLayerEmpty(blockLayer) && athleteLayer)
        return athleteLayer;
    if (isLayerEmpty(athleteLayer) && blockLayer)
        return blockLayer;
    if (!blockLayer || !athleteLayer) {
        // This can't happen, but I couldn't get typescript guards to cooperate
        throw new Error("Failed to ensure at least 1 layer wasn't undefined");
    }
    // Process set metrics specially (merge by [workoutSetId, metricId] combination)
    const blockSetMetricsMap = new Map(blockLayer.setMetrics.map((sm) => [
        `${sm.workoutSetId}-${sm.metricId}`,
        sm,
    ]));
    const athleteSetMetricsMap = new Map(athleteLayer.setMetrics.map((sm) => [
        `${sm.workoutSetId}-${sm.metricId}`,
        sm,
    ]));
    const finalSetMetrics = [
        ...Array.from(blockSetMetricsMap.values()).filter((sm) => !athleteSetMetricsMap.has(`${sm.workoutSetId}-${sm.metricId}`)),
        ...athleteLayer.setMetrics,
    ];
    return {
        sectionAdds: deduplicateSortOrders(mergeWithEdits('section', blockLayer.sectionAdds, athleteLayer.sectionAdds, athleteLayer.sectionEdits, athleteLayer.removals, 'workoutSectionId')),
        sectionEdits: [],
        exerciseAdds: deduplicateSortOrders(mergeWithEdits('exercise', blockLayer.exerciseAdds, athleteLayer.exerciseAdds, athleteLayer.exerciseEdits, athleteLayer.removals, 'workoutExerciseId')),
        exerciseEdits: [],
        exerciseMetricAdds: deduplicateMetricSortOrders(mergeWithoutEdits('exerciseMetric', blockLayer.exerciseMetricAdds, athleteLayer.exerciseMetricAdds, athleteLayer.removals, 'workoutExerciseMetricId')),
        setAdds: deduplicateSortOrders(mergeWithEdits('set', blockLayer.setAdds, athleteLayer.setAdds, athleteLayer.setEdits, athleteLayer.removals, 'workoutSetId')),
        setEdits: [],
        setMetrics: finalSetMetrics,
        supersetAdds: deduplicateSortOrders(mergeWithEdits('superset', blockLayer.supersetAdds, athleteLayer.supersetAdds, athleteLayer.supersetEdits, athleteLayer.removals, 'workoutSupersetId')),
        supersetEdits: [],
        supersetExerciseAdds: deduplicateSortOrders(mergeWithEdits('supersetExercise', blockLayer.supersetExerciseAdds, athleteLayer.supersetExerciseAdds, athleteLayer.supersetExerciseEdits, athleteLayer.removals, 'workoutSupersetExerciseId')),
        supersetExerciseEdits: [],
        alternateGroupAdds: deduplicateSortOrders(mergeWithEdits('alternateGroup', blockLayer.alternateGroupAdds, athleteLayer.alternateGroupAdds, athleteLayer.alternateGroupEdits, athleteLayer.removals, 'workoutAlternateGroupId')),
        alternateGroupEdits: [],
        alternateExerciseAdds: deduplicateSortOrders(mergeWithEdits('alternateExercise', blockLayer.alternateExerciseAdds, athleteLayer.alternateExerciseAdds, athleteLayer.alternateExerciseEdits, athleteLayer.removals, 'workoutAlternateExerciseId')),
        alternateExerciseEdits: [],
        removals: [],
    };
}
export function isLayerEmpty(layer) {
    const l = layer && 'layer' in layer ? layer.layer : layer;
    if (!l)
        return true;
    const result = !Object.keys(l).some((key) => {
        return l[key].length > 0;
    });
    return result;
}
// Merge athlete Adds/Edits into block Adds
function mergeWithEdits(entityType, blockAdds, athleteAdds, athleteEdits, athleteRemovals, idField) {
    const withEdits = applyEditsToAdds(blockAdds, athleteEdits, idField);
    const filtered = filterRemovals(withEdits, athleteRemovals, entityType, idField);
    return [...filtered, ...athleteAdds];
}
// Concatenate athlete Adds onto block Adds (for components with no Edits counterpart)
function mergeWithoutEdits(entityType, blockAdds, athleteAdds, athleteRemovals, idField) {
    const filtered = filterRemovals(blockAdds, athleteRemovals, entityType, idField);
    return [...filtered, ...athleteAdds];
}
// Merge athlete layer Edits into block layer Adds
function applyEditsToAdds(adds, edits, idField) {
    const editsById = new Map(edits.map((e) => [e[idField], e]));
    return adds.map((add) => {
        const edit = editsById.get(add[idField]);
        if (!edit)
            return add;
        const merged = { ...add };
        for (const prop in edit) {
            if (prop !== 'id' && edit[prop] !== undefined && edit[prop] !== null) {
                merged[prop] = edit[prop];
            }
        }
        return merged;
    });
}
// Remove from the block layer if it's present in 'removals' in the athlete layer
function filterRemovals(items, removals, entityType, idField) {
    const removedIds = new Set(removals.filter((r) => r.entityType === entityType).map((r) => r.entityId));
    return items.filter((item) => !removedIds.has(item[idField]));
}
/**
 * Resolve any duplicate sort orders between the block and athlete layer.
 * It is assumed that what's passed in here is an item list that is a merge
 * of a block and athlete layer, where any athlete items come after block items.
 */
function deduplicateSortOrders(items) {
    if (items.length === 0)
        return items;
    const uniqueSortOrders = new Set(items.map((item) => item.sortOrder));
    if (uniqueSortOrders.size === items.length) {
        // no duplicates
        return items;
    }
    return items
        .map((item, originalIndex) => ({ item, originalIndex }))
        .sort((a, b) => {
        if (a.item.sortOrder !== b.item.sortOrder) {
            return a.item.sortOrder - b.item.sortOrder;
        }
        // if sort orders are the same, use array order
        return a.originalIndex - b.originalIndex;
    })
        .map((item, index) => {
        return {
            ...item.item,
            sortOrder: index,
        };
    });
}
// Accounts for the sets metric
function deduplicateMetricSortOrders(items) {
    if (items.length === 0)
        return items;
    // separate Sets metrics and set the sort order to -100 in case it isn't
    const setsMetrics = items
        .filter((item) => item.metricName === 'Sets')
        .map((metric) => ({
        ...metric,
        sortOrder: -100,
    }));
    const nonSetsMetrics = items.filter((item) => item.metricName !== 'Sets');
    const metrics = deduplicateSortOrders(nonSetsMetrics);
    return [...setsMetrics, ...metrics];
}
//# sourceMappingURL=merge-athlete-to-block-layer.js.map